import React from 'react';
import { useSelector } from 'react-redux';
import { useCachedFriends } from '../../../hooks/useCachedFriends';
import Button from '../../../components/ui/Button';

/**
 * FriendSelector Component
 * Displays a list of friends (users from recent conversations) for selection
 * When selected, a Send button appears below the user to send directly to them
 */
const FriendSelector = ({ selectedFriends = [], onToggleSelection, onMoreClick, onSendToUser, sendingToUser, sentFriends = [] }) => {
    const currentUser = useSelector(state => state.auth.currentUser);
    const { friends, loading, refreshFriends, error } = useCachedFriends(currentUser);

    const handleFriendClick = (friendId) => {
        if (onToggleSelection) {
            onToggleSelection(friendId);
        }
    };

    const handleSendClick = (e, friendId) => {
        e.stopPropagation(); // Prevent toggling selection
        if (onSendToUser) {
            onSendToUser(friendId);
        }
    };

    if (loading) {
        return (
            <div className="py-3 text-center text-gray-400 text-sm">
                <p>Loading...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="py-3 text-center text-sm">
                <p className="text-red-400 mb-2">Failed to load friends</p>
                <Button variant="secondary" size="sm" onClick={refreshFriends}>
                    Retry
                </Button>
            </div>
        );
    }

    if (friends.length === 0) {
        return (
            <div className="py-3 text-center text-gray-400 text-sm">
                <button
                    type="button"
                    className="mt-3 px-4 py-2 bg-transparent border border-dashed border-gray-300 rounded-full text-primary text-sm font-semibold hover:bg-gray-50 hover:border-primary hover:text-blue-600 transition-all duration-200 shrink-0"
                    onClick={onMoreClick}
                >
                    Search users
                </button>
            </div>
        );
    }

    return (
        <div className="py-2">
            <div className="flex items-start gap-2.5 overflow-x-auto overflow-y-visible flex-nowrap pb-2 scrollbar-none md:scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                {friends.map(friend => {
                    const friendId = friend.id || friend.uid;
                    const isSelected = selectedFriends.includes(friendId);
                    const displayName = friend.displayName || friend.username || friend.name || 'User';
                    const photoURL = friend.photoURL || friend.userPhotoURL || null;
                    const isSending = sendingToUser === friendId;
                    const isSent = sentFriends.includes(friendId);

                    return (
                        <div key={friendId} className="flex flex-col items-center gap-1.5 shrink-0">
                            <button
                                type="button"
                                className={`flex flex-row items-center gap-2.5 rounded-full border-none cursor-pointer p-1.5 pr-3.5 transition-all duration-200 shrink-0 ${
                                    isSelected ? 'bg-primary' : 'bg-gray-100 hover:bg-gray-200'
                                }`}
                                onClick={() => handleFriendClick(friendId)}
                                title={displayName}
                            >
                                <div className={`w-8 h-8 rounded-full overflow-hidden shrink-0 border-2 transition-all duration-200 ${
                                    isSelected ? 'border-white' : 'border-transparent'
                                }`}>
                                    {photoURL ? (
                                        <img src={photoURL} alt={displayName} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-sm font-semibold uppercase">
                                            {displayName.charAt(0)}
                                        </div>
                                    )}
                                </div>
                                <div className={`text-sm font-medium max-w-[100px] truncate text-left ${
                                    isSelected ? 'text-white font-semibold' : 'text-gray-700'
                                }`}>
                                    {displayName}
                                </div>
                            </button>
                            {/* Send button appears below selected user */}
                            {isSelected && (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={(e) => handleSendClick(e, friendId)}
                                    disabled={isSending || isSent}
                                >
                                    {isSent ? 'Sent' : (isSending ? '...' : 'Send')}
                                </Button>
                            )}
                        </div>
                    );
                })}

                <button
                    type="button"
                    className="p-2 px-4 bg-transparent border border-dashed border-gray-300 rounded-full text-primary text-sm font-semibold hover:bg-gray-50 hover:border-primary hover:text-blue-600 transition-all duration-200 shrink-0"
                    onClick={onMoreClick}
                >
                    Search users
                </button>
            </div>
        </div>
    );
};

export default FriendSelector;
